import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { projectTimelines, ProjectTimeline, TimelineStep } from '../data/timelineData';
import { 
  Clock, 
  CheckCircle, 
  Hammer, 
  PenTool, 
  Truck, 
  Wrench, 
  Search, 
  ShieldCheck 
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { fadeIn, fadeInUp } from '@/utils/animations';
import { useOnScreen } from '@/hooks/useOnScreen';

interface ProjectTimelineSectionProps {
  onContactClick: () => void;
}

const ProjectTimelineSection = ({ onContactClick }: ProjectTimelineSectionProps) => {
  const [selectedProject, setSelectedProject] = useState<ProjectTimeline>(projectTimelines[0]);
  const sectionRef = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(sectionRef, 0.1);
  
  // Map step titles to icons
  const getStepIcon = (title: string) => {
    const iconMap: Record<string, JSX.Element> = {
      'Initial Consultation': <Clock className="w-6 h-6" />,
      'Project Assessment': <Search className="w-6 h-6" />,
      'Damage Assessment': <Search className="w-6 h-6" />,
      'Design Consultation': <PenTool className="w-6 h-6" />,
      'Design & Approval': <PenTool className="w-6 h-6" />,
      'Concept Development': <PenTool className="w-6 h-6" />,
      'Engineering & Planning': <PenTool className="w-6 h-6" />,
      'Material Selection': <Truck className="w-6 h-6" />,
      'Material Procurement': <Truck className="w-6 h-6" />,
      'Material Acquisition': <Truck className="w-6 h-6" />,
      'Fabrication': <Hammer className="w-6 h-6" />,
      'Fabrication Process': <Hammer className="w-6 h-6" />,
      'Prefabrication': <Hammer className="w-6 h-6" />,
      'Preparation': <Wrench className="w-6 h-6" />,
      'On-site Welding': <Wrench className="w-6 h-6" />,
      'Repair Work': <Wrench className="w-6 h-6" />,
      'Installation': <Wrench className="w-6 h-6" />,
      'Refinishing': <Wrench className="w-6 h-6" />,
      'Quality Testing': <ShieldCheck className="w-6 h-6" />,
      'Quality Control': <ShieldCheck className="w-6 h-6" />,
      'Final Inspection': <CheckCircle className="w-6 h-6" />,
      'Delivery & Installation': <Truck className="w-6 h-6" />,
      'Cost Estimation': <Calculator className="w-6 h-6" />,
    };
    
    // Return the matching icon or a default
    return iconMap[title] || <Clock className="w-6 h-6" />;
  };

  // Add icons to each step
  const timelineWithIcons = selectedProject.steps.map(step => ({
    ...step,
    icon: getStepIcon(step.title)
  }));

  return (
    <motion.div 
      ref={sectionRef}
      id="timeline"
      className="py-20 bg-gray-50"
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      variants={fadeIn}
    >
      <div className="container mx-auto px-4">
        <motion.div className="text-center mb-16" variants={fadeInUp}>
          <h2 className="text-4xl font-bold mb-4">Project Timeline</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Understand the process and timeline for your welding project. Each project follows a structured approach to ensure quality results.
          </p>
        </motion.div>

        {/* Project Selector */}
        <motion.div 
          className="mb-12 flex flex-wrap justify-center gap-4"
          variants={fadeInUp}
        >
          {projectTimelines.map((project) => (
            <button
              key={project.id}
              onClick={() => setSelectedProject(project)}
              className={cn(
                "px-6 py-3 rounded-lg font-medium transition-all duration-300",
                selectedProject.id === project.id 
                  ? "bg-red-600 text-white shadow-md" 
                  : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-100"
              )}
            >
              {project.title}
            </button>
          ))}
        </motion.div>
        
        {/* Selected Project Info */}
        <motion.div 
          className="bg-white rounded-xl shadow-lg p-8 mb-12"
          variants={fadeInUp}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h3 className="text-2xl font-bold">{selectedProject.title}</h3>
              <p className="text-gray-600">{selectedProject.description}</p>
            </div>
            <div className="bg-gray-100 px-4 py-2 rounded-full">
              <span className="font-semibold">Total Duration:</span> {selectedProject.totalDuration}
            </div>
          </div>
        </motion.div>

        {/* Timeline */}
        <motion.div 
          className="relative"
          variants={fadeInUp}
        >
          {/* Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gray-200 hidden md:block" />
          
          {/* Timeline Steps */}
          {timelineWithIcons.map((step, index) => (
            <motion.div 
              key={index}
              className="relative mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 + 0.2 }}
            >
              <div className={cn(
                "flex flex-col md:flex-row items-center",
                index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
              )}>
                {/* Timeline Icon (visible on mobile) */}
                <div className="md:hidden bg-red-600 p-3 rounded-full text-white mb-4">
                  {step.icon}
                </div>
                
                {/* Content */}
                <div className={cn(
                  "bg-white rounded-xl shadow-md p-6 md:w-5/12",
                  index % 2 === 0 ? "md:text-right" : "md:text-left"
                )}>
                  <h4 className="text-xl font-bold mb-2">{step.title}</h4>
                  <div className="text-red-600 font-medium mb-2">Duration: {step.duration}</div>
                  <p className="text-gray-600">{step.description}</p>
                </div>
                
                {/* Center icon (hidden on mobile) */}
                <div className="hidden md:flex items-center justify-center bg-red-600 p-3 rounded-full text-white absolute left-1/2 transform -translate-x-1/2 z-10">
                  {step.icon}
                </div>
                
                {/* Empty space for the other side */}
                <div className="md:w-5/12" />
              </div>
            </motion.div>
          ))}
        </motion.div>
        
        {/* CTA */}
        <motion.div 
          className="text-center mt-16"
          variants={fadeInUp}
        >
          <h3 className="text-2xl font-bold mb-4">Ready to start your project?</h3>
          <button 
            onClick={onContactClick}
            className="px-8 py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-900 transition duration-300"
          >
            Get Started Today
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ProjectTimelineSection;

// Additional icon that doesn't come from lucide-react by default
const Calculator = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <line x1="8" y1="6" x2="16" y2="6" />
    <line x1="8" y1="10" x2="10" y2="10" />
    <line x1="14" y1="10" x2="16" y2="10" />
    <line x1="8" y1="14" x2="10" y2="14" />
    <line x1="14" y1="14" x2="16" y2="14" />
    <line x1="8" y1="18" x2="10" y2="18" />
    <line x1="14" y1="18" x2="16" y2="18" />
  </svg>
);